import { Capacitor } from "@capacitor/core";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { AppHeader } from "../components/AppHeader";
import { PageShell } from "../components/PageShell";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { useAuth } from "../lib/auth/auth-context";
import { buildRedirectUrl } from "../lib/auth/deep-link";
import { isReviewEmail, signInWithReviewPassword } from "../lib/auth/magic-link";

type FormStatus = "idle" | "sending" | "sent" | "error";

const EmailSchema = z.string().email().max(254);
// Supabase Auth impose un throttle interne (~60s) entre 2 magic links pour le
// même email. On désactive le bouton pendant ce délai pour éviter l'erreur
// "you can only request this after X seconds".
const RESEND_COOLDOWN_S = 60;

function currentRedirectUrl(): string | undefined {
  return buildRedirectUrl({
    isNative: Capacitor.isNativePlatform(),
    origin: typeof window !== "undefined" ? window.location.origin : undefined,
  });
}

function submitLabel({
  reviewMode,
  status,
  cooldownS,
}: {
  reviewMode: boolean;
  status: FormStatus;
  cooldownS: number;
}): string {
  if (reviewMode) return status === "sending" ? "Connexion…" : "Se connecter";
  if (status === "sending") return "Envoi…";
  if (cooldownS > 0) return `Renvoyer dans ${cooldownS}s`;
  return status === "sent" ? "Renvoyer le lien" : "Envoyer le magic link";
}

export function SignIn() {
  const { signIn, client } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cooldownS, setCooldownS] = useState(0);
  // Seul le compte de revue Apple voit le champ mot de passe ; toute autre
  // adresse garde le lien magique, inchangé.
  const reviewMode = isReviewEmail(email);

  useEffect(() => {
    if (cooldownS <= 0) return;
    const id = setInterval(() => {
      setCooldownS((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownS]);

  // Passer du lien magique au mot de passe (ou l'inverse) efface l'issue du
  // mode précédent : son erreur ou sa confirmation ne concerne plus ce champ.
  function handleEmailChange(next: string) {
    if (isReviewEmail(next) !== reviewMode) {
      setStatus("idle");
      setErrorMessage(null);
    }
    setEmail(next);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);

    if (reviewMode) {
      setStatus("sending");
      const result = await signInWithReviewPassword(client, email, password);
      if (result.ok) {
        navigate("/", { replace: true });
      } else {
        setStatus("error");
        setErrorMessage(result.error);
      }
      return;
    }

    const parsed = EmailSchema.safeParse(email);
    if (!parsed.success) {
      setStatus("error");
      setErrorMessage("Adresse courriel invalide");
      return;
    }

    setStatus("sending");
    const result = await signIn(parsed.data, { redirectTo: currentRedirectUrl() });

    if (result.ok) {
      setStatus("sent");
      setCooldownS(RESEND_COOLDOWN_S);
    } else {
      setStatus("error");
      setErrorMessage(result.error);
    }
  }

  const isCoolingDown = cooldownS > 0;
  const isDisabled = status === "sending" || (isCoolingDown && !reviewMode);
  const buttonLabel = submitLabel({ reviewMode, status, cooldownS });

  return (
    <PageShell width="narrow" center>
      {/* Hero — la flamme Flambeau accueille la connexion */}
      <AppHeader
        variant="plain"
        title="Connexion"
        subtitle="Reçois un lien magique pour entrer dans Écoute."
      />

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <Input
          label="Courriel"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          disabled={isDisabled}
          placeholder="vous@exemple.com"
          error={status === "error" && !reviewMode ? errorMessage : undefined}
        />
        {reviewMode && (
          <Input
            label="Mot de passe"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isDisabled}
            error={status === "error" ? errorMessage : undefined}
          />
        )}
        <Button type="submit" variant="primary" size="md" block disabled={isDisabled}>
          {buttonLabel}
        </Button>
      </form>

      {status === "sent" && (
        <Card variant="success">
          <p role="status" className="m-0 text-small">
            Vérifie ton courriel pour le lien de connexion (peut prendre 30 s).
          </p>
        </Card>
      )}

      {/* Pied discret : liens légaux, consultables sans connexion. */}
      <footer className="pt-8 text-center text-small text-muted">
        <Link to="/conditions" className="underline hover:text-ink">
          Conditions d'utilisation
        </Link>
        <span aria-hidden="true"> · </span>
        <Link to="/confidentialite" className="underline hover:text-ink">
          Vos données
        </Link>
      </footer>
    </PageShell>
  );
}
